/**
 * Reprise de données par fichier Excel ou CSV (Q91–Q93, décision 15) : champs
 * importables par entité, bornes d'un fichier et lecture des valeurs telles
 * qu'un tableur français les écrit (« 12,50 € », « 01/03/2024 », SIRET avec
 * espaces). Le serveur applique ces règles ligne par ligne ; l'assistant
 * d'import les annonce avant l'envoi.
 */
import {
  ARTICLE_CATEGORIES, ARTICLE_UNITS, CUSTOMER_KINDS, EQUIPMENT_CATEGORIES,
  type ArticleCategory, type ArticleUnit, type CustomerKind, type ImportEntity,
} from './domain.js';
import { EMAIL_INVALID_MESSAGE, parseEmailInput } from './email.js';
import { fr } from './i18n/fr.js';
import { CUSTOMER_CONTACT_ROLES, type CustomerContactRole } from './roles.js';

/** Bornes appliquées par le serveur à chaque import, avant toute écriture. */
export const IMPORT_LIMITS = {
  /** Fichier déposé. */
  maxFileBytes: 10 * 1024 * 1024,
  /** Lignes de données par fichier, en-tête non compris. */
  maxRows: 5000,
  /** Colonnes décrites dans la correspondance. */
  maxMappedColumns: 60,
  /** Longueur d'un en-tête de colonne. */
  maxHeaderChars: 200,
  /** Classeur .xlsx une fois décompressé (c'est une archive zip). */
  maxUnpackedBytes: 40 * 1024 * 1024,
  /** Fichiers contenus dans un classeur .xlsx. */
  maxArchiveEntries: 1000,
  /** Texte total lu dans les colonnes retenues. */
  maxTextChars: 10_000_000,
  /** Colonnes rendues par l'aperçu d'un fichier. */
  maxPreviewColumns: 200,
  /** Lignes de données rendues par l'aperçu, pour relier les colonnes. */
  previewRows: 5,
} as const;

/** Nature d'un fichier déposé pour un import (contrôlée au dépôt par le serveur). */
export const IMPORT_FILE_KIND = 'import';
/** Types acceptés au dépôt : CSV (Windows l'annonce parfois comme Excel) et classeur .xlsx. */
export const IMPORT_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export const IMPORT_FILE_REFUSED = 'Seuls les fichiers .csv et .xlsx s’importent (un .xls : enregistrez-le en .xlsx ou en CSV).';

/** États d'un import vus par l'assistant : simulé à valider, importé, ou annulé. */
export const IMPORT_JOB_STATES = ['simule', 'applique', 'annule'] as const;
export type ImportJobState = (typeof IMPORT_JOB_STATES)[number];

export const IMPORT_ROW_STATUSES = ['ok', 'doublon', 'erreur'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

/** Compte rendu d'un import : la simulation, puis l'application et l'annulation du lot. */
export interface ImportReport {
  total: number;
  ok: number;
  doublons: number;
  erreurs: number;
  /** Fiches créées à l'application. */
  appliques?: number;
  /** Lignes retenues mais non écrites (fiche identique créée entre-temps, client disparu…). */
  ignores?: number;
  /** Fiches retirées à l'annulation du lot. */
  retires?: number;
  /** Fiches gardées à l'annulation : modifiées ou utilisées depuis l'import. */
  conserves?: number;
  /** Sites créés par un import de machines et retirés avec lui. */
  sitesRetires?: number;
}

/** Un import, tel que la liste et la page d'un import le montrent. */
export interface ImportJobSummary {
  id: string;
  entity: ImportEntity;
  state: ImportJobState;
  report: ImportReport | null;
  fileName: string | null;
  createdBy: string | null;
  createdAt: string;
  appliedAt: string | null;
  cancelledAt: string | null;
  /** Lignes par issue actuelle (page d'un import seulement) : une ligne non écrite passe en erreur. */
  rowCounts?: Record<ImportRowStatus, number>;
}

/** Une ligne du fichier avec son issue. */
export interface ImportRowItem {
  id: string;
  rowNumber: number;
  status: ImportRowStatus;
  message: string | null;
  values: Record<string, string>;
  targetId: string | null;
  cancelOutcome: 'retiree' | 'conservee' | null;
}

export interface ImportField {
  key: string;
  label: string;
  required: boolean;
}

/** Champs que chaque entité sait recevoir, dans l'ordre d'un modèle de fichier. */
export const IMPORT_FIELDS: Record<ImportEntity, readonly ImportField[]> = {
  clients: [
    { key: 'display_name', label: 'Nom du client', required: true },
    { key: 'kind', label: 'Type (professionnel ou particulier)', required: false },
    { key: 'siret', label: 'SIRET', required: false },
    { key: 'contact_name', label: 'Contact', required: false },
    { key: 'phone', label: 'Téléphone', required: false },
    { key: 'email', label: 'E-mail', required: false },
    { key: 'billing_address', label: 'Adresse de facturation', required: false },
    { key: 'notes', label: 'Notes', required: false },
  ],
  sites: [
    { key: 'customer_name', label: 'Nom du client', required: true },
    { key: 'site_label', label: 'Site', required: true },
    { key: 'address', label: 'Adresse du site', required: true },
    { key: 'local_contact_name', label: 'Contact sur place', required: false },
    { key: 'local_contact_phone', label: 'Téléphone sur place', required: false },
  ],
  contacts: [
    { key: 'customer_name', label: 'Nom du client', required: true },
    { key: 'full_name', label: 'Nom du contact', required: true },
    { key: 'role', label: 'Rôle (principal, technique ou facturation)', required: false },
    { key: 'email', label: 'E-mail', required: false },
    { key: 'phone', label: 'Téléphone', required: false },
  ],
  equipements: [
    { key: 'customer_name', label: 'Nom du client', required: true },
    { key: 'site_label', label: 'Site', required: true },
    { key: 'address', label: 'Adresse du site', required: false },
    { key: 'category', label: 'Type de machine', required: true },
    { key: 'brand', label: 'Marque', required: false },
    { key: 'model', label: 'Modèle', required: false },
    { key: 'serial_number', label: 'N° de série', required: false },
    { key: 'installed_on', label: 'Mise en service (JJ/MM/AAAA)', required: false },
    { key: 'fluid_type', label: 'Fluide', required: false },
    { key: 'fluid_charge_grams', label: 'Charge de fluide (g)', required: false },
    { key: 'gwp', label: 'PRP du fluide', required: false },
  ],
  fournisseurs: [
    { key: 'name', label: 'Nom du fournisseur', required: true },
    { key: 'contact_name', label: 'Contact', required: false },
    { key: 'phone', label: 'Téléphone', required: false },
    { key: 'email', label: 'E-mail', required: false },
    { key: 'address', label: 'Adresse', required: false },
  ],
  articles: [
    { key: 'reference', label: 'Référence', required: true },
    { key: 'label', label: 'Libellé', required: true },
    { key: 'category', label: 'Type d’article', required: false },
    { key: 'unit', label: 'Unité', required: false },
    { key: 'purchase_price', label: 'Prix d’achat HT (€)', required: false },
    { key: 'sale_price', label: 'Prix de vente HT (€)', required: false },
    { key: 'reorder_threshold', label: 'Seuil de réapprovisionnement', required: false },
  ],
};

export interface ImportedCustomer {
  kind: CustomerKind;
  displayName: string;
  siret: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  billingAddress: string | null;
  notes: string | null;
}

export interface ImportedSite {
  customerName: string;
  siteLabel: string;
  address: string;
  localContactName: string | null;
  localContactPhone: string | null;
}

export interface ImportedContact {
  customerName: string;
  fullName: string;
  role: CustomerContactRole;
  email: string | null;
  phone: string | null;
}

export interface ImportedEquipment {
  customerName: string;
  siteLabel: string;
  address: string | null;
  category: string;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  installedOn: string | null;
  fluidType: string | null;
  fluidChargeGrams: number | null;
  gwp: number | null;
}

export interface ImportedSupplier {
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
}

export interface ImportedArticle {
  reference: string;
  label: string;
  category: ArticleCategory;
  unit: ArticleUnit;
  purchasePriceCents: number | null;
  salePriceCents: number | null;
  reorderThresholdMilli: number;
}

export interface ImportedRecords {
  clients: ImportedCustomer;
  sites: ImportedSite;
  contacts: ImportedContact;
  equipements: ImportedEquipment;
  fournisseurs: ImportedSupplier;
  articles: ImportedArticle;
}

export type ImportRowResult<E extends ImportEntity> =
  | { ok: true; value: ImportedRecords[E] }
  | { ok: false; message: string };

/** Mêmes plafonds que la saisie à l'écran. */
const MAX_PRICE_CENTS = 100_000_000;
const MAX_QTY_MILLI = 1_000_000_000;
const MAX_CHARGE_GRAMS = 1_000_000;
const MAX_GWP = 100_000;

/** Minuscules sans accents ni ponctuation de liaison, pour reconnaître un libellé. */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_\-’'/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function choices<T extends string>(codes: readonly T[], aliases: Record<string, T>): Map<string, T> {
  const map = new Map<string, T>();
  for (const code of codes) map.set(fold(code), code);
  for (const [alias, code] of Object.entries(aliases)) map.set(fold(alias), code);
  return map;
}

const CUSTOMER_KIND_CHOICES = choices(CUSTOMER_KINDS, { pro: 'professionnel', entreprise: 'professionnel', part: 'particulier' });
const CONTACT_ROLE_CHOICES = choices(CUSTOMER_CONTACT_ROLES, {
  ...Object.fromEntries(Object.entries(fr.contacts.roles).map(([code, label]) => [label, code as CustomerContactRole])),
  principal: 'administrateur', comptabilite: 'facturation', compta: 'facturation', factures: 'facturation',
});
const ARTICLE_CATEGORY_CHOICES = choices(ARTICLE_CATEGORIES, {
  ...Object.fromEntries(Object.entries(fr.articles.categories).map(([code, label]) => [label, code as ArticleCategory])),
  pièce: 'piece', fluide: 'gaz_fluide',
});
const ARTICLE_UNIT_CHOICES = choices(ARTICLE_UNITS, {
  unités: 'unite', u: 'unite', pce: 'unite', kilo: 'kg', kilos: 'kg', kilogramme: 'kg', kilogrammes: 'kg',
  l: 'litre', litres: 'litre',
});
const EQUIPMENT_CATEGORY_CHOICES = choices(EQUIPMENT_CATEGORIES, Object.fromEntries(
  Object.entries(fr.equipements.categories).map(([code, label]) => [label, code as (typeof EQUIPMENT_CATEGORIES)[number]]),
));

/** Espaces, y compris insécables, que les tableurs insèrent dans les nombres. */
const NUMBER_SPACES = /[\s\u00a0\u202f]/g;

/**
 * Nombre écrit à la française ou par un tableur : « 1 234,56 », « 12,5 €»,
 * « 1.234,56 », « 12.5 ». Le dernier séparateur est la virgule décimale quand
 * les deux apparaissent ; `null` si ce n'est pas un nombre.
 */
export function parseFrenchNumber(raw: string): number | null {
  const text = raw.replace(NUMBER_SPACES, '').replace(/^€|€$/g, '');
  if (!text) return null;
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  let normalized = text;
  if (lastComma >= 0 && lastDot >= 0) {
    const decimal = lastComma > lastDot ? ',' : '.';
    const thousands = decimal === ',' ? '.' : ',';
    normalized = text.split(thousands).join('').replace(decimal, '.');
  } else if (lastComma >= 0) {
    if (text.indexOf(',') !== lastComma) return null;
    normalized = text.replace(',', '.');
  } else if (lastDot >= 0 && text.indexOf('.') !== lastDot) {
    normalized = text.split('.').join('');
  }
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

/** Valeur décimale convertie en unités entières (centimes, millièmes) ; `null` si elle tombe entre deux. */
function scaled(value: number, factor: number): number | null {
  const result = Math.round(value * factor);
  return Math.abs(value * factor - result) > 1e-6 ? null : result;
}

/**
 * Date d'un tableur français (JJ/MM/AAAA, JJ-MM-AAAA, JJ.MM.AAAA) ou ISO
 * (AAAA-MM-JJ, forme qu'une cellule date Excel prend à la lecture) ;
 * `null` si elle n'existe pas au calendrier.
 */
export function parseFrenchDate(raw: string): string | null {
  const text = raw.trim();
  const french = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(text);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const parts = french ? [french[3], french[2], french[1]] : iso ? [iso[1], iso[2], iso[3]] : null;
  if (!parts) return null;
  const [year, month, day] = parts.map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  if (year < 1900 || year > 2100) return null;
  return date.toISOString().slice(0, 10);
}

/** Lecteur d'une ligne : chaque problème est noté avec le nom du champ, la ligne reste lisible. */
class RowReader {
  readonly problems: string[] = [];

  constructor(
    private readonly row: Record<string, string>,
    private readonly fields: readonly ImportField[],
  ) {}

  private label(key: string): string {
    return this.fields.find((field) => field.key === key)?.label ?? key;
  }

  private fail(key: string, problem: string): null {
    this.problems.push(`${this.label(key)} : ${problem}`);
    return null;
  }

  text(key: string, max: number): string | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) {
      return this.fields.find((field) => field.key === key)?.required ? this.fail(key, 'obligatoire') : null;
    }
    return value.length > max ? this.fail(key, `${max} caractères au plus`) : value;
  }

  required(key: string, max: number): string {
    return this.text(key, max) ?? '';
  }

  choice<T extends string>(key: string, map: Map<string, T>, fallback: T, allowed: string): T {
    const value = (this.row[key] ?? '').trim();
    if (!value) return fallback;
    const known = map.get(fold(value));
    if (known) return known;
    this.fail(key, `« ${value.slice(0, 60)} » inconnu (${allowed})`);
    return fallback;
  }

  email(key: string): string | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) return null;
    const email = parseEmailInput(value);
    return email ? email : this.fail(key, EMAIL_INVALID_MESSAGE);
  }

  siret(key: string): string | null {
    const value = (this.row[key] ?? '').replace(/[\s\u00a0\u202f.-]/g, '');
    if (!value) return null;
    return /^\d{14}$/.test(value)
      ? value
      : this.fail(key, '14 chiffres attendus (dans Excel, mettez la colonne au format Texte pour garder le 0 de tête)');
  }

  /** Montant en euros, converti en centimes. */
  euros(key: string): number | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) return null;
    const amount = parseFrenchNumber(value);
    if (amount === null || amount < 0) return this.fail(key, `montant en euros attendu, reçu « ${value.slice(0, 30)} »`);
    const cents = scaled(amount, 100);
    if (cents === null) return this.fail(key, 'au centime près');
    return cents > MAX_PRICE_CENTS ? this.fail(key, 'montant trop élevé') : cents;
  }

  /** Quantité décimale, convertie en millièmes. */
  quantity(key: string): number | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) return null;
    const qty = parseFrenchNumber(value);
    if (qty === null || qty < 0) return this.fail(key, `quantité attendue, reçu « ${value.slice(0, 30)} »`);
    const milli = scaled(qty, 1000);
    if (milli === null) return this.fail(key, 'trois décimales au plus');
    return milli > MAX_QTY_MILLI ? this.fail(key, 'quantité trop élevée') : milli;
  }

  integer(key: string, max: number): number | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) return null;
    const n = parseFrenchNumber(value);
    if (n === null || !Number.isInteger(n) || n < 0) {
      return this.fail(key, `nombre entier attendu, reçu « ${value.slice(0, 30)} »`);
    }
    return n > max ? this.fail(key, `${max} au plus`) : n;
  }

  date(key: string): string | null {
    const value = (this.row[key] ?? '').trim();
    if (!value) return null;
    return parseFrenchDate(value) ?? this.fail(key, `date JJ/MM/AAAA attendue, reçu « ${value.slice(0, 30)} »`);
  }
}

const READERS: { [E in ImportEntity]: (r: RowReader) => ImportedRecords[E] } = {
  clients: (r) => ({
    displayName: r.required('display_name', 200),
    kind: r.choice('kind', CUSTOMER_KIND_CHOICES, 'professionnel', 'professionnel ou particulier'),
    siret: r.siret('siret'),
    contactName: r.text('contact_name', 200),
    phone: r.text('phone', 30),
    email: r.email('email'),
    billingAddress: r.text('billing_address', 500),
    notes: r.text('notes', 4000),
  }),
  // Mêmes bornes que la saisie d'un site ou d'un contact sur la fiche client.
  sites: (r) => ({
    customerName: r.required('customer_name', 200),
    siteLabel: r.required('site_label', 200),
    address: r.required('address', 500),
    localContactName: r.text('local_contact_name', 200),
    localContactPhone: r.text('local_contact_phone', 30),
  }),
  contacts: (r) => ({
    customerName: r.required('customer_name', 200),
    fullName: r.required('full_name', 200),
    role: r.choice('role', CONTACT_ROLE_CHOICES, 'technique', 'principal, technique ou facturation'),
    email: r.email('email'),
    phone: r.text('phone', 30),
  }),
  equipements: (r) => {
    const category = r.required('category', 100);
    return {
      customerName: r.required('customer_name', 200),
      siteLabel: r.required('site_label', 200),
      address: r.text('address', 500),
      // Un type connu prend son code ; un autre libellé est gardé tel quel, comme à la saisie.
      category: EQUIPMENT_CATEGORY_CHOICES.get(fold(category)) ?? category,
      brand: r.text('brand', 100),
      model: r.text('model', 100),
      serialNumber: r.text('serial_number', 200),
      installedOn: r.date('installed_on'),
      fluidType: r.text('fluid_type', 40),
      fluidChargeGrams: r.integer('fluid_charge_grams', MAX_CHARGE_GRAMS),
      gwp: r.integer('gwp', MAX_GWP),
    };
  },
  fournisseurs: (r) => ({
    name: r.required('name', 120),
    contactName: r.text('contact_name', 120),
    phone: r.text('phone', 40),
    email: r.email('email'),
    address: r.text('address', 500),
  }),
  articles: (r) => ({
    reference: r.required('reference', 60),
    label: r.required('label', 160),
    category: r.choice('category', ARTICLE_CATEGORY_CHOICES, 'piece', 'pièce, machine, fluide ou consommable'),
    unit: r.choice('unit', ARTICLE_UNIT_CHOICES, 'unite', 'unité, kg ou litre'),
    purchasePriceCents: r.euros('purchase_price'),
    salePriceCents: r.euros('sale_price'),
    reorderThresholdMilli: r.quantity('reorder_threshold') ?? 0,
  }),
};

/** Lit une ligne du fichier pour une entité : la valeur prête à enregistrer, ou les problèmes à corriger. */
export function readImportRow<E extends ImportEntity>(entity: E, row: Record<string, string>): ImportRowResult<E> {
  const reader = new RowReader(row, IMPORT_FIELDS[entity]);
  const value = READERS[entity](reader);
  return reader.problems.length > 0
    ? { ok: false, message: reader.problems.join(' ; ') }
    : { ok: true, value };
}

/**
 * Autres en-têtes courants d'un fichier exporté d'un tableur ou d'un autre
 * logiciel, reconnus pour proposer la correspondance (le libellé du champ
 * l'est toujours). Une proposition se corrige avant la simulation.
 */
const FIELD_ALIASES: Partial<Record<ImportEntity, Record<string, readonly string[]>>> = {
  clients: {
    display_name: ['nom', 'client', 'raison sociale', 'societe', 'nom client', 'denomination'],
    kind: ['type', 'type de client', 'categorie'],
    contact_name: ['nom du contact', 'interlocuteur'],
    phone: ['tel', 'telephone fixe', 'portable', 'mobile'],
    email: ['mail', 'courriel', 'adresse e mail', 'adresse email'],
    billing_address: ['adresse', 'adresse complete'],
    notes: ['commentaire', 'commentaires', 'remarques', 'observations'],
  },
  sites: {
    customer_name: ['client', 'nom client', 'raison sociale'],
    site_label: ['nom du site', 'lieu', 'etablissement'],
    address: ['adresse', 'adresse complete'],
    local_contact_name: ['contact', 'contact site', 'responsable'],
    local_contact_phone: ['telephone', 'tel', 'telephone du site'],
  },
  contacts: {
    customer_name: ['client', 'nom client', 'raison sociale'],
    full_name: ['nom', 'contact', 'nom et prenom', 'prenom et nom', 'interlocuteur'],
    role: ['role', 'fonction'],
    phone: ['tel', 'portable', 'mobile'],
    email: ['mail', 'courriel'],
  },
  equipements: {
    customer_name: ['client', 'nom client', 'raison sociale'],
    site_label: ['nom du site', 'lieu'],
    address: ['adresse'],
    category: ['type', 'categorie', 'machine', 'equipement'],
    serial_number: ['numero de serie', 'n serie', 'serie', 'sn'],
    installed_on: ['mise en service', 'date de mise en service', 'installation', 'date d installation'],
    fluid_type: ['fluide frigorigene', 'refrigerant', 'gaz'],
    fluid_charge_grams: ['charge', 'charge (g)', 'charge en g'],
    gwp: ['prp', 'gwp'],
  },
  fournisseurs: {
    name: ['nom', 'fournisseur', 'raison sociale'],
    contact_name: ['interlocuteur'],
    phone: ['tel'],
    email: ['mail', 'courriel'],
  },
  articles: {
    reference: ['ref', 'code', 'code article'],
    label: ['designation', 'description', 'nom'],
    category: ['type', 'categorie', 'famille'],
    unit: ['unite de vente'],
    purchase_price: ['prix d achat', 'prix achat', 'achat ht', 'pa ht'],
    sale_price: ['prix de vente', 'prix vente', 'vente ht', 'pv ht'],
    reorder_threshold: ['seuil', 'stock minimum', 'stock mini'],
  },
};

/** Libellé sans sa précision entre parenthèses : « Mise en service (JJ/MM/AAAA) » → « mise en service ». */
function bareLabel(label: string): string {
  return fold(label.replace(/\s*\([^)]*\)\s*$/, ''));
}

/**
 * Correspondance proposée « champ → colonne du fichier » : un en-tête égal au
 * libellé du champ (avec ou sans sa précision), puis à un nom courant de ce
 * champ, sans casse ni accents. Chaque colonne ne sert qu'une fois.
 */
export function suggestImportMapping(entity: ImportEntity, headers: readonly string[]): Record<string, string> {
  const folded = headers.map((header) => fold(header));
  const used = new Set<number>();
  const result: Record<string, string> = {};
  const take = (key: string, candidates: string[]): void => {
    if (result[key] !== undefined) return;
    const at = folded.findIndex((header, i) => !used.has(i) && header !== '' && candidates.includes(header));
    if (at >= 0) {
      used.add(at);
      result[key] = headers[at]!;
    }
  };
  const fields = IMPORT_FIELDS[entity];
  // Libellés exacts d'abord (modèle de MyEquip), puis les noms courants.
  for (const field of fields) take(field.key, [fold(field.label), bareLabel(field.label)]);
  for (const field of fields) take(field.key, (FIELD_ALIASES[entity]?.[field.key] ?? []).map(fold));
  return result;
}

/** Modèle de fichier d'une entité : la ligne d'en-tête en CSV pour Excel (point-virgule, BOM UTF-8). */
export function importTemplateCsv(entity: ImportEntity): string {
  return `\uFEFF${IMPORT_FIELDS[entity].map((field) => field.label).join(';')}\r\n`;
}
